import React from "react";
import PropTypes from "prop-types";
import { CheckIcon, XMarkIcon, InformationCircleIcon } from "@heroicons/react/24/outline";

function Row({ feature, isEven }) {
  const getCellContent = (row, cellIndex) => {
    if (row.label === "Check") {
      return (
        <div className="inline-flex items-center">
          <div className="flex size-6 items-center justify-center rounded-full bg-green-500/20 ring-2 ring-green-400/50">
            <CheckIcon className="size-4 text-green-400" />
          </div>
          <span className="ml-3 font-semibold text-green-400">Yes</span>
        </div>
      );
    }
    if (row.label === "Cancel") {
      return (
        <div className="inline-flex items-center">
          <div className="flex size-6 items-center justify-center rounded-full bg-red-500/20 ring-2 ring-red-400/50">
            <XMarkIcon className="size-4 text-red-400" />
          </div>
          <span className="ml-3 font-semibold text-red-400">No</span>
          {row.popoverText && (
            <div className="group relative ml-2">
              <InformationCircleIcon className="size-4 cursor-help text-gray-500 transition-colors duration-200 hover:text-blue-400" />
              <div
                className={`pointer-events-none absolute bottom-full z-30 mb-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100 ${
                  cellIndex >= 2 ? "right-0" : "left-1/2 -translate-x-1/2"
                }`}
              >
                <div className="max-w-xs break-words rounded-lg border border-gray-600 bg-gray-800 px-4 py-3 text-xs text-gray-200 shadow-xl backdrop-blur-sm">
                  {row.popoverText}
                  <div
                    className={`absolute top-full border-4 border-transparent border-t-gray-800 ${
                      cellIndex >= 2 ? "right-4" : "left-1/2 -translate-x-1/2"
                    }`}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
    return <span className="font-semibold text-gray-300">{row.label}</span>;
  };

  const getFundviewCellContent = row => {
    if (row.label === "Check") {
      return (
        <div className="flex items-center">
          <div className="flex size-7 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-500 shadow-lg">
            <CheckIcon className="size-4 text-white" />
          </div>
          <span className="ml-3 font-bold text-blue-400">Included</span>
        </div>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full border border-blue-400/30 bg-gradient-to-r from-blue-500/20 to-purple-500/20 px-4 py-2 text-sm font-semibold text-blue-300 backdrop-blur-sm">
        {row.label}
      </span>
    );
  };

  return (
    <tr className={`${isEven ? "bg-gray-800/40" : "bg-gray-800/20"} transition-all duration-300 hover:bg-gray-700/50`}>
      <td className="px-8 py-6">
        <div className="flex items-center">
          <div className="flex-1">
            <span className="text-lg font-bold text-white">{feature.label}</span>
            {feature.popoverText && (
              <div className="group relative ml-3 inline-block">
                <InformationCircleIcon className="size-5 cursor-help text-gray-500 transition-colors duration-200 hover:text-blue-400" />
                <div className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-3 -translate-x-1/2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                  <div className="max-w-sm break-words rounded-lg border border-gray-600 bg-gray-800 px-4 py-3 text-xs text-gray-200 shadow-xl backdrop-blur-sm">
                    {feature.popoverText}
                    <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-8 py-6 text-center">
        {getCellContent(feature.rows[1], 1)} {/* Basic CRM */}
      </td>
      <td className="px-8 py-6 text-center">
        {getCellContent(feature.rows[2], 2)} {/* Off-the-Shelf CRMs */}
      </td>
      <td className="px-8 py-6">
        {getFundviewCellContent(feature.rows[0])} {/* Fundview */}
      </td>
    </tr>
  );
}

Row.propTypes = {
  feature: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    popoverText: PropTypes.string,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        popoverText: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
  isEven: PropTypes.bool.isRequired,
};

export default Row;
